import { yupResolver } from "@hookform/resolvers/yup";
import {
  Grid,
  MenuItem,
  TextField,
  Typography
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { GetCity, GetState } from "react-country-state-city";
import { Controller, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import CustomButton from "../../../../Components/CustomButton";
import Fonts from "../../../../Config/Fonts";
import { userSchema } from "../../../../Services/ValidationSchema";
import { getRoleAPI } from "../../../../Store/Action/rolePermissionAction";
import Colors from "../../../../theme/Colors";

const UserForm = ({ onSubmit }) => {
  const dispatch = useDispatch();
  const [stateList, setStateList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [stateId, setStateId] = useState(0);
  const [cityId, setCityId] = useState(0);
  const countryId = 101; // India is selected by default
  const [roleList, setRoleList] = useState([]); // State for roles
  const [selectedRoleId, setSelectedRoleId] = useState(""); // State for selected role
  const [showGSTFields, setShowGSTFields] = useState(true);
  const [showCompanyDocFields, setShowCompanyDocFields] = useState(false);
  const [selectedOption, setSelectedOption] = useState("yes");

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(userSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      userEmail: "",
      userMobileNo: "",
      userAddress: "",
      shopName: "",
      state: null,
      city: null,
      role: null,
      gstOption: "",
      GSTNo: "",
      CompanyDoc: "",
      panCardNo: "",
      pinCode: "",
    },
    mode: "onBlur",
  });

  useEffect(() => {
    dispatch(
      getRoleAPI({}, (result) => {
        setRoleList(result);
      })
    );
  }, [dispatch]);

  useEffect(() => {
    if (countryId) {
      GetState(countryId).then((result) => {
        setStateList(result);
      });
    }
  }, [countryId]);

  const handleStateChange = useCallback((e) => {
    const selectedStateId = parseInt(e.target.value);
    setStateId(selectedStateId);
    setCityId(0);
    GetCity(countryId, selectedStateId).then((result) => {
      setCityList(result);
    });
  }, []);

  const handleCityChange = useCallback((e) => {
    const selectedCityId = parseInt(e.target.value);
    setCityId(selectedCityId);
  }, []);

  const handleRoleChange = useCallback((e) => {
    setSelectedRoleId(e.target.value);
  }, []);

  const renderRequiredAsterisk = useCallback(
    () => <span style={requiredAsteriskStyle}>*</span>,
    []
  );

  const handleGSTOptionChange = (option) => {
    setSelectedOption(option);
    if (option === "yes") {
      setShowGSTFields(true);
      setShowCompanyDocFields(false);
    } else {
      setShowGSTFields(false);
      setShowCompanyDocFields(true);
    }
  };

  const handleFormSubmit = (data) => {


    const selectedState =
      stateList.find((state) => state.id === stateId)?.name || "";
    const selectedCity =
      cityList.find((city) => city.id === cityId)?.name || "";

    const formData = new FormData();
    formData.append("user_first_name", data.firstName);
    formData.append("user_last_name", data.lastName);
    formData.append("user_email", data.userEmail);
    formData.append("user_mobile_number", data.userMobileNo);
    formData.append("user_address", data.userAddress);
    formData.append("user_shop_name", data.shopName);

    if (data.gstOption === "yes") {
      formData.append("GST_No", data.GSTNo);
    } else if (data.gstOption === "no") {
      formData.append("company_doc_no", data.CompanyDoc);
    }

    formData.append("pan_card_no", data.panCardNo);
    formData.append("pin_code", data.pinCode);

    onSubmit({
      formData,
      user_role_id: selectedRoleId,
      user_state: selectedState,
      user_city: selectedCity,
      gst_checkbox_option: data.gstOption,
    });
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} style={{ padding: "20px" }}>
      <Grid container spacing={2}>
        {/* First Name, Last Name, Shop Name */}
        <Grid container item xs={12} spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle1" style={inputLabelFontStyle}>
              First Name {renderRequiredAsterisk()}
            </Typography>
            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder="Enter First Name"
                  variant="outlined"
                  fullWidth
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                  InputProps={{ style: inputTextStyle }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle1" style={inputLabelFontStyle}>
              Last Name {renderRequiredAsterisk()}
            </Typography>
            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder="Enter Last Name"
                  variant="outlined"
                  fullWidth
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                  InputProps={{ style: inputTextStyle }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle1" style={inputLabelFontStyle}>
              Shop Name {renderRequiredAsterisk()}
            </Typography>
            <Controller
              name="shopName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder="Enter Shop Name"
                  variant="outlined"
                  fullWidth
                  error={!!errors.shopName}
                  helperText={errors.shopName?.message}
                  InputProps={{ style: inputTextStyle }}
                />
              )}
            />
          </Grid>
        </Grid>

        {/* Mobile No, Alternate Mobile No */}
        <Grid container item xs={12} spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle1" style={inputLabelFontStyle}>
              Enter Mobile Number {renderRequiredAsterisk()}
            </Typography>
            <Controller
              name="userMobileNo"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder="Enter Mobile Number"
                  variant="outlined"
                  fullWidth
                  error={!!errors.userMobileNo}
                  helperText={errors.userMobileNo?.message}
                  InputProps={{ style: inputTextStyle }}
                  inputProps={{ maxLength: 10 }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle1" style={inputLabelFontStyle}>
              Enter Alternate Mobile Number
            </Typography>
            <Controller
              name="alternateMobileNo"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder="Enter Alternate Mobile Number"
                  variant="outlined"
                  fullWidth
                  InputProps={{ style: inputTextStyle }}
                />
              )}
            />
          </Grid>
        </Grid>

        {/* Email, PAN No */}
        <Grid container item xs={12} spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle1" style={inputLabelFontStyle}>
              Enter Email Address {renderRequiredAsterisk()}
            </Typography>
            <Controller
              name="userEmail"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder="Enter email address"
                  variant="outlined"
                  fullWidth
                  error={!!errors.userEmail}
                  helperText={errors.userEmail?.message}
                  InputProps={{ style: inputTextStyle }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle1" style={inputLabelFontStyle}>
             Enter PAN Number {renderRequiredAsterisk()}
            </Typography>
            <Controller
              name="panCardNo"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder="Enter Pan Number"
                  variant="outlined"
                  fullWidth
                  error={!!errors.panCardNo}
                  helperText={errors.panCardNo?.message}
                  InputProps={{ style: inputTextStyle }}
                />
              )}
            />
          </Grid>
        </Grid>

        {/* Role, Address */}
        <Grid container item xs={12} spacing={2} style={{ width: "100%" }}>
          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle1" style={inputLabelFontStyle}>
              Role
            </Typography>
            <Controller
              name="role"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  variant="outlined"
                  value={selectedRoleId}
                  onChange={handleRoleChange}
                  error={!!errors.role}
                  helperText={errors.role?.message}
                  style={inputTextStyle} // Apply styling here
                >
                  {roleList?.map((item) => (
                    <MenuItem
                      key={item?.user_role_id}
                      value={item?.user_role_id}
                    >
                      {item?.user_role_name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle1" style={inputLabelFontStyle}>
              Address {renderRequiredAsterisk()}
            </Typography>
            <Controller
              name="userAddress"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder="Address"
                  variant="outlined"
                  error={!!errors.userAddress}
                  helperText={errors.userAddress?.message}
                  style={inputTextStyle} // Apply styling here
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle1" style={inputLabelFontStyle}>
              State {renderRequiredAsterisk()}
            </Typography>
            <Controller
              name="state"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  variant="outlined"
                  value={stateId}
                  onChange={handleStateChange}
                  error={!!errors.state}
                  helperText={errors.state?.message}
                  style={inputTextStyle} // Apply styling here
                >
                  {stateList.map((state) => (
                    <MenuItem key={state.id} value={state.id}>
                      {state.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle1" style={inputLabelFontStyle}>
              City {renderRequiredAsterisk()}
            </Typography>
            <Controller
              name="city"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  variant="outlined"
                  value={cityId}
                  onChange={(e) => {
                    handleCityChange(e);
                  }}
                  error={!!errors.city}
                  helperText={errors.city?.message}
                  style={inputTextStyle} // Apply styling here
                  SelectProps={{ displayEmpty: true }} // Allow empty value
                >
                  {cityList.map((city) => (
                    <MenuItem key={city.id} value={city.id}>
                      {city.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
        </Grid>

        {/* GST Yes/No */}
        <Grid
          container
          item
          xs={20}
          spacing={2}
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
          }}
        >
          <Grid item xs={6}>
            <Typography variant="subtitle1" style={inputLabelFontStyle}>
              Are you a GST Holder? {renderRequiredAsterisk()}
            </Typography>
            <div
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              <div
                onClick={() => handleGSTOptionChange("yes")}
                style={{
                  width: "50%",
                  padding: 16,
                  border: `1px solid ${
                    selectedOption === "yes"
                      ? Colors.adminPrimaryColor
                      : "#363740"
                  }`,
                  backgroundColor:
                    selectedOption === "yes"
                      ? Colors.adminPrimaryColor
                      : Colors.white,
                  borderRadius: 4,
                  cursor: "pointer",
                  textAlign: "center",
                  marginRight: "10px",
                }}
              >
                <p
                  style={{
                    color:
                      selectedOption === "yes" ? Colors.white : Colors.black,
                    fontSize: 16,
                    fontWeight: "600",
                    fontFamily: Fonts.Poppins,
                  }}
                >
                  Yes
                </p>
              </div>

              <div
                onClick={() => handleGSTOptionChange("no")}
                style={{
                  width: "50%",
                  padding: 16,
                  border: `1px solid ${
                    selectedOption === "no"
                      ? Colors.adminPrimaryColor
                      : "#363740"
                  }`,
                  backgroundColor:
                    selectedOption === "no"
                      ? Colors.adminPrimaryColor
                      : Colors.white,
                  borderRadius: 4,
                  cursor: "pointer",
                  textAlign: "center",
                }}
              >
                <p
                  style={{
                    color:
                      selectedOption === "no" ? Colors.white : Colors.black,
                    fontSize: 16,
                    fontWeight: "600",
                    fontFamily: Fonts.Poppins,
                  }}
                >
                  No
                </p>
              </div>
            </div>
          </Grid>

          {showGSTFields && (
            <Grid container item xs={6} spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" style={inputLabelFontStyle}>
                  GST Number {renderRequiredAsterisk()}
                </Typography>
                <Controller
                  name="GSTNo"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      placeholder="GST Number"
                      variant="outlined"
                      fullWidth
                      error={!!errors.GSTNo}
                      helperText={errors.GSTNo?.message}
                      InputProps={{ style: inputTextStyle }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          )}

          {showCompanyDocFields && (
            <Grid container item xs={6} spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" style={inputLabelFontStyle}>
                  Company Document Number {renderRequiredAsterisk()}
                </Typography>
                <Controller
                  name="CompanyDoc"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      placeholder="Company Document Number"
                      variant="outlined"
                      fullWidth
                      error={!!errors.CompanyDoc}
                      helperText={errors.CompanyDoc?.message}
                      InputProps={{ style: inputTextStyle }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          )}
        </Grid>

        {/* Save Button */}
        <Grid container item xs={12} spacing={2}>
          <Grid item xs={12}>
            <CustomButton label={'Save'} />
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
};

// Styles
const inputLabelFontStyle = {
  textAlign: "left",
  fontSize: 16,
  fontWeight: "500",
  fontFamily: Fonts.Poppins,
  color: Colors.headingBlackColor,
};

const inputTextStyle = {
  width: "100%",
  textAlign: 'left',
  fontSize: 14,
  fontWeight: "400",
  fontFamily: Fonts.Poppins,
  color: Colors.paginationTitle,
};

const requiredAsteriskStyle = {
  color: "red",
};

export default UserForm;
